import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.js';

// ── Types ─────────────────────────────────────────────────────────────────────

type CropRect = { x: number; y: number; w: number; h: number }; // natural-image pixels
type Handle = 'tl' | 'tr' | 'bl' | 'br' | 'body' | 'none';
type Phase = 'idle' | 'capturing' | 'crop' | 'uploading' | 'done';

type Preset = { label: string; top: number; right: number; bottom: number; left: number }; // px to trim

const BUILT_IN_PRESETS: Preset[] = [
  { label: 'capture.presetFull', top: 0, right: 0, bottom: 0, left: 0 },
  // Chrome/Edge macOS: tabbar ≈ 37px, addressbar ≈ 50px → trim ~90px top
  { label: 'capture.presetNoBrowserBar', top: 90, right: 0, bottom: 0, left: 0 },
];
const PRESETS_STORAGE_KEY = 'simultrain-capture-presets';
const HANDLE_RADIUS = 7;
const HANDLE_HIT = 18; // hit area radius

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadCustomPresets(): Preset[] {
  try {
    return JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveCustomPresets(presets: Preset[]) {
  localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(presets));
}

function clampRect(r: CropRect, imgW: number, imgH: number): CropRect {
  const x = Math.max(0, Math.min(r.x, imgW - 1));
  const y = Math.max(0, Math.min(r.y, imgH - 1));
  const w = Math.max(10, Math.min(r.w, imgW - x));
  const h = Math.max(10, Math.min(r.h, imgH - y));
  return { x, y, w, h };
}

function applyPreset(preset: Preset, imgW: number, imgH: number): CropRect {
  return clampRect(
    {
      x: preset.left,
      y: preset.top,
      w: imgW - preset.left - preset.right,
      h: imgH - preset.top - preset.bottom,
    },
    imgW,
    imgH,
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

type Props = {
  versionId: string;
  onCaptured: (assetId: string) => void;
  onClose: () => void;
};

export function CaptureModal({ versionId, onCaptured, onClose }: Props) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [naturalW, setNaturalW] = useState(0);
  const [naturalH, setNaturalH] = useState(0);
  const [crop, setCrop] = useState<CropRect>({ x: 0, y: 0, w: 0, h: 0 });
  const [customPresets, setCustomPresets] = useState<Preset[]>(loadCustomPresets);
  const [newPresetName, setNewPresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rawBlobRef = useRef<Blob | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Scale from natural to canvas-display coords.
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });

  const dragRef = useRef<{
    handle: Handle;
    startX: number; startY: number;
    startCrop: CropRect;
  } | null>(null);

  // ── Draw ────────────────────────────────────────────────────────────────────

  const draw = useCallback((c: CropRect) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d')!;
    const scale = scaleRef.current;
    const off = offsetRef.current;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, off.x, off.y, naturalW * scale, naturalH * scale);

    // Dark overlay outside crop.
    const cx = off.x + c.x * scale;
    const cy = off.y + c.y * scale;
    const cw = c.w * scale;
    const ch = c.h * scale;

    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.clearRect(cx, cy, cw, ch); // punch hole
    ctx.drawImage(img,
      c.x, c.y, c.w, c.h,
      cx, cy, cw, ch);

    // Crop border.
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.strokeRect(cx, cy, cw, ch);

    // Rule-of-thirds grid inside crop.
    ctx.strokeStyle = 'rgba(56,189,248,0.3)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 3; i++) {
      ctx.beginPath(); ctx.moveTo(cx + (cw / 3) * i, cy); ctx.lineTo(cx + (cw / 3) * i, cy + ch); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx, cy + (ch / 3) * i); ctx.lineTo(cx + cw, cy + (ch / 3) * i); ctx.stroke();
    }

    // Corner handles.
    const corners: [number, number][] = [
      [cx, cy], [cx + cw, cy], [cx, cy + ch], [cx + cw, cy + ch],
    ];
    for (const [hx, hy] of corners) {
      ctx.fillStyle = '#38bdf8';
      ctx.beginPath();
      ctx.arc(hx, hy, HANDLE_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [naturalW, naturalH]);

  useEffect(() => {
    if (phase === 'crop') draw(crop);
  }, [crop, phase, draw]);

  // ── Capture ─────────────────────────────────────────────────────────────────

  async function startCapture() {
    setError(null);
    setPhase('capturing');
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 1 } },
        audio: false,
      });
      const track = stream.getVideoTracks()[0]!;
      const settings = track.getSettings();
      const capW = settings.width ?? 1920;
      const capH = settings.height ?? 1080;

      // Capture a single frame via an offscreen video element.
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await new Promise<void>((res) => { video.onloadedmetadata = () => res(); });
      await video.play();
      // Wait two animation frames so the first real frame is painted.
      await new Promise((res) => requestAnimationFrame(res));
      await new Promise((res) => requestAnimationFrame(res));

      const offscreen = document.createElement('canvas');
      offscreen.width = video.videoWidth || capW;
      offscreen.height = video.videoHeight || capH;
      offscreen.getContext('2d')!.drawImage(video, 0, 0);
      const blob = await new Promise<Blob>((res, rej) =>
        offscreen.toBlob((b) => (b ? res(b) : rej(new Error('toBlob failed'))), 'image/png'),
      );

      rawBlobRef.current = blob;
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        setNaturalW(w);
        setNaturalH(h);
        setCrop({ x: 0, y: 0, w, h });
        setPhase('crop');
        // Size canvas after state update.
        requestAnimationFrame(() => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          const maxW = canvas.parentElement!.clientWidth - 32;
          const maxH = window.innerHeight * 0.6;
          const scale = Math.min(1, maxW / w, maxH / h);
          scaleRef.current = scale;
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          offsetRef.current = { x: 0, y: 0 };
          draw({ x: 0, y: 0, w, h });
        });
      };
      img.src = url;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // User cancelled the picker — treat as silent cancel.
      if (msg.includes('Permission denied') || msg.includes('NotAllowedError')) {
        setPhase('idle');
      } else {
        setError(t('capture.error') + ': ' + msg);
        setPhase('idle');
      }
    } finally {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    }
  }

  // ── Mouse crop interaction ───────────────────────────────────────────────────

  function toNatural(canvasX: number, canvasY: number) {
    const off = offsetRef.current;
    const s = scaleRef.current;
    return {
      x: (canvasX - off.x) / s,
      y: (canvasY - off.y) / s,
    };
  }

  function hitHandle(cx: number, cy: number, c: CropRect): Handle {
    const scale = scaleRef.current;
    const off = offsetRef.current;
    const corners: [Handle, number, number][] = [
      ['tl', off.x + c.x * scale, off.y + c.y * scale],
      ['tr', off.x + (c.x + c.w) * scale, off.y + c.y * scale],
      ['bl', off.x + c.x * scale, off.y + (c.y + c.h) * scale],
      ['br', off.x + (c.x + c.w) * scale, off.y + (c.y + c.h) * scale],
    ];
    for (const [handle, hx, hy] of corners) {
      if (Math.hypot(cx - hx, cy - hy) <= HANDLE_HIT) return handle;
    }
    const scale2 = scale;
    const rx = off.x + c.x * scale2;
    const ry = off.y + c.y * scale2;
    const rw = c.w * scale2;
    const rh = c.h * scale2;
    if (cx >= rx && cx <= rx + rw && cy >= ry && cy <= ry + rh) return 'body';
    return 'none';
  }

  function onMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current!.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const handle = hitHandle(cx, cy, crop);
    if (handle === 'none') {
      // Start drawing new crop rect.
      const nat = toNatural(cx, cy);
      dragRef.current = { handle: 'tl', startX: cx, startY: cy, startCrop: { x: nat.x, y: nat.y, w: 0, h: 0 } };
      setCrop({ x: nat.x, y: nat.y, w: 1, h: 1 });
    } else {
      dragRef.current = { handle, startX: cx, startY: cy, startCrop: { ...crop } };
    }
  }

  function onMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const d = dragRef.current;
    if (!d) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const dx = (cx - d.startX) / scaleRef.current;
    const dy = (cy - d.startY) / scaleRef.current;
    const s = d.startCrop;
    let next: CropRect = { ...s };

    switch (d.handle) {
      case 'body': next = { x: s.x + dx, y: s.y + dy, w: s.w, h: s.h }; break;
      case 'tl':
        if (d.startCrop.w === 0) {
          // Drawing new rect.
          const nat = toNatural(cx, cy);
          next = {
            x: Math.min(d.startCrop.x, nat.x),
            y: Math.min(d.startCrop.y, nat.y),
            w: Math.abs(nat.x - d.startCrop.x),
            h: Math.abs(nat.y - d.startCrop.y),
          };
        } else {
          next = { x: s.x + dx, y: s.y + dy, w: s.w - dx, h: s.h - dy };
        }
        break;
      case 'tr': next = { x: s.x, y: s.y + dy, w: s.w + dx, h: s.h - dy }; break;
      case 'bl': next = { x: s.x + dx, y: s.y, w: s.w - dx, h: s.h + dy }; break;
      case 'br': next = { x: s.x, y: s.y, w: s.w + dx, h: s.h + dy }; break;
    }
    const clamped = clampRect(next, naturalW, naturalH);
    setCrop(clamped);
    draw(clamped);
  }

  function onMouseUp() {
    dragRef.current = null;
  }

  function cursorForPos(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const h = hitHandle(cx, cy, crop);
    const cursors: Record<Handle, string> = {
      tl: 'nwse-resize', tr: 'nesw-resize',
      bl: 'nesw-resize', br: 'nwse-resize',
      body: 'move', none: 'crosshair',
    };
    canvasRef.current.style.cursor = cursors[h];
  }

  // ── Upload ───────────────────────────────────────────────────────────────────

  async function confirm() {
    if (!rawBlobRef.current || !imageRef.current) return;
    setPhase('uploading');
    try {
      // Apply crop: draw region into a new canvas, export as PNG.
      const out = document.createElement('canvas');
      out.width = Math.round(crop.w);
      out.height = Math.round(crop.h);
      out.getContext('2d')!.drawImage(
        imageRef.current,
        crop.x, crop.y, crop.w, crop.h,
        0, 0, crop.w, crop.h,
      );
      const croppedBlob = await new Promise<Blob>((res, rej) =>
        out.toBlob((b) => (b ? res(b) : rej(new Error('toBlob failed'))), 'image/png'),
      );
      const asset = await api.uploadAsset(versionId, croppedBlob, 'screenshot.png');
      onCaptured(asset.id);
    } catch (err) {
      setError(t('capture.error') + ': ' + String(err));
      setPhase('crop');
    }
  }

  // ── Presets ─────────────────────────────────────────────────────────────────

  function applyPresetByValue(p: Preset) {
    const newCrop = applyPreset(p, naturalW, naturalH);
    setCrop(newCrop);
    requestAnimationFrame(() => draw(newCrop));
  }

  function savePreset() {
    if (!newPresetName.trim()) return;
    const p: Preset = {
      label: newPresetName.trim(),
      top: crop.y,
      left: crop.x,
      right: naturalW - crop.x - crop.w,
      bottom: naturalH - crop.y - crop.h,
    };
    const updated = [...customPresets, p];
    setCustomPresets(updated);
    saveCustomPresets(updated);
    setNewPresetName('');
    setShowSavePreset(false);
  }

  function deleteCustomPreset(i: number) {
    const updated = customPresets.filter((_, idx) => idx !== i);
    setCustomPresets(updated);
    saveCustomPresets(updated);
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--panel)', borderRadius: 10, padding: 20,
        width: '100%', maxWidth: 1000, maxHeight: '90vh',
        overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{t('capture.title')}</h2>
          <button className="secondary" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {/* ── Phase: idle ── */}
        {(phase === 'idle' || phase === 'capturing') && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ color: 'var(--muted)', marginBottom: 24, maxWidth: 480, margin: '0 auto 24px' }}>
              {t('capture.instructions')}
            </p>
            <button onClick={startCapture} disabled={phase === 'capturing'} style={{ fontSize: 16, padding: '12px 28px' }}>
              {phase === 'capturing' ? '…' : t('capture.start')}
            </button>
            <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 16 }}>
              {t('capture.permissionHint')}
            </p>
          </div>
        )}

        {/* ── Phase: crop ── */}
        {phase === 'crop' && (
          <>
            {/* Preset buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span style={{ color: 'var(--muted)', fontSize: 13 }}>{t('capture.presets')}:</span>
              {BUILT_IN_PRESETS.map((p) => (
                <button key={p.label} className="secondary" style={{ padding: '4px 10px', fontSize: 13 }}
                  onClick={() => applyPresetByValue(p)}>
                  {t(p.label)}
                </button>
              ))}
              {customPresets.map((p, i) => (
                <span key={i} style={{ display: 'flex', gap: 2 }}>
                  <button className="secondary" style={{ padding: '4px 10px', fontSize: 13 }}
                    onClick={() => applyPresetByValue(p)}>{p.label}</button>
                  <button className="danger" style={{ padding: '4px 6px', fontSize: 11 }}
                    onClick={() => deleteCustomPreset(i)}>✕</button>
                </span>
              ))}
              <button className="secondary" style={{ padding: '4px 10px', fontSize: 13 }}
                onClick={() => setShowSavePreset((v) => !v)}>
                {t('capture.presetSave')}
              </button>
            </div>

            {showSavePreset && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder={t('capture.presetNamePlaceholder')}
                  style={{ width: 200 }}
                  onKeyDown={(e) => e.key === 'Enter' && savePreset()}
                />
                <button onClick={savePreset} disabled={!newPresetName.trim()}>
                  {t('capture.presetSaveConfirm')}
                </button>
              </div>
            )}

            {/* Canvas */}
            <div style={{ background: '#0b1220', borderRadius: 8, padding: 8, display: 'flex', justifyContent: 'center' }}>
              <canvas
                ref={canvasRef}
                onMouseDown={onMouseDown}
                onMouseMove={(e) => { onMouseMove(e); cursorForPos(e); }}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                style={{ display: 'block', maxWidth: '100%' }}
              />
            </div>

            {/* Crop dimensions */}
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>
              {Math.round(crop.w)} × {Math.round(crop.h)} px
              &nbsp;·&nbsp;
              {t('capture.cropHint')}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="secondary" onClick={() => setPhase('idle')}>{t('capture.recapture')}</button>
              <button onClick={confirm}>{t('capture.confirm')}</button>
            </div>
          </>
        )}

        {/* ── Phase: uploading ── */}
        {phase === 'uploading' && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
            {t('capture.uploading')}
          </div>
        )}
      </div>
    </div>
  );
}
